import { useSyncExternalStore } from 'react';

export type ToastType = 'error' | 'success' | 'info';

export interface ToastOptions {
  message: string;
  type?: ToastType;
  /** Auto-dismiss delay in ms. Defaults to 4000. */
  duration?: number;
}

export interface ToastItem {
  id: string;
  message: string;
  type: ToastType;
  duration: number;
  /** How many identical (same type + message) toasts collapsed into this one. */
  count: number;
}

export const DEFAULT_TOAST_DURATION = 4000;

// Module-level store so that non-React callers (e.g. the TanStack Query
// global error handlers in queryClientFactory) can trigger toasts without a
// hook. ToastProvider subscribes to it via useSyncExternalStore.
let items: ToastItem[] = [];
let counter = 0;
const listeners = new Set<() => void>();

const emit = () => {
  listeners.forEach(listener => listener());
};

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

const getSnapshot = () => items;

const dismiss = (id: string) => {
  const next = items.filter(item => item.id !== id);
  if (next.length === items.length) {
    return;
  }
  items = next;
  emit();
};

const show = ({
  message,
  type = 'info',
  duration = DEFAULT_TOAST_DURATION
}: ToastOptions): string => {
  // Collapse an identical (same type + message) active toast instead of
  // stacking a duplicate — a burst of failing queries must not produce N copies
  // of the same message (and N screen-reader announcements). The bumped `count`
  // also refreshes the entry so its auto-dismiss timer resets.
  const existing = items.find(
    item => item.type === type && item.message === message
  );
  if (existing) {
    items = items.map(item =>
      item.id === existing.id ? { ...item, count: item.count + 1 } : item
    );
    emit();
    return existing.id;
  }

  counter += 1;
  const id = `toast-${counter}`;
  items = [...items, { id, message, type, duration, count: 1 }];
  emit();
  return id;
};

/**
 * Imperative toast API. Usable from anywhere — React components should prefer
 * the {@link useToast} hook, but module-level code can import this directly.
 */
export const toast = { show, dismiss };

/** Internal: list of active toasts, kept in sync via the external store. */
export const useToastItems = (): ToastItem[] =>
  useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

/**
 * Imperative API for showing transient user feedback.
 *
 * @example
 * const { show } = useToast();
 * show({ type: 'error', message: 'Žao nam je, nismo uspeli da sačuvamo kupon' });
 */
export const useToast = () => toast;
